import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useApp } from "../store";
import { AddressCard, PageHeader, PrimaryButton } from "../components/UI";

import "../css/Addresses.css";
const emptyForm = {
  name: "",
  mobile: "",
  flat: "",
  building: "",
  area: "",
  pincode: "",
  type: "Home",
};

export default function Addresses() {
  const navigate = useNavigate();
  const location = useLocation();
  const { addresses, saveAddress, deleteAddress, user } = useApp();
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const returnTo = new URLSearchParams(location.search).get("returnTo");

  function openAdd() {
    setEditing(null);
    setForm(emptyForm);
    setError("");
    setShow(true);
  }

  function openEdit(address) {
    const parts = (address.line || "").split(",");
    setEditing(address.id);
    setForm({
      name: address.name || "",
      mobile: address.mobile || "",
      flat: parts[0]?.trim() || "",
      building: parts.slice(1).join(",").trim(),
      area: address.area || "",
      pincode: address.pincode || "",
      type: address.type || "Home",
    });
    setError("");
    setShow(true);
  }

  function change(name, value) {
    setForm((current) => ({ ...current, [name]: value }));
  }

  async function submit(event) {
    event.preventDefault();
    if (!user?.uid) {
      navigate(
        `/login?redirect=${encodeURIComponent(location.pathname + location.search)}`,
      );
      return;
    }
    setSaving(true);
    setError("");
    try {
      const saved = await saveAddress({
        id: editing || undefined,
        type: form.type,
        name: form.name,
        mobile: form.mobile,
        line: `${form.flat}, ${form.building}`,
        area: form.area,
        city: "Pune",
        pincode: form.pincode,
      });
      setShow(false);
      if (returnTo && saved?.id && !editing) {
        const separator = returnTo.includes("?") ? "&" : "?";
        navigate(
          `${returnTo}${separator}addressId=${encodeURIComponent(saved.id)}`,
          { replace: true },
        );
      }
    } catch (err) {
      setError(err?.message || "Unable to save address.");
    } finally {
      setSaving(false);
    }
  }

  async function remove(id) {
    if (!window.confirm("Delete this address?")) return;
    try {
      await deleteAddress(id);
    } catch (err) {
      setError(err?.message || "Unable to delete address.");
    }
  }

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="page-wrap narrow container-xl">
      <PageHeader
        title="Saved addresses"
        copy="Manage your delivery addresses."
      />
      <div className="d-grid gap-3">
        {addresses.map((address) => (
          <AddressCard
            key={address.id}
            address={address}
            onEdit={openEdit}
            onDelete={remove}
          />
        ))}
      </div>

      <button
        type="button"
        className="btn btn-brand w-100 mt-4"
        onClick={openAdd}
      >
        <i className="fa-solid fa-plus me-2" />
        Add new address
      </button>

      {show && (
        <form className="form-card mt-4" onSubmit={submit}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2>{editing ? "Edit address" : "Add an address"}</h2>
            <button
              type="button"
              className="icon-button"
              onClick={() => setShow(false)}
            >
              <i className="fa-solid fa-xmark" />
            </button>
          </div>
          <div className="row g-3">
            {Object.entries({
              name: "Full name",
              mobile: "Mobile number",
              flat: "Flat / House number",
              building: "Building name",
              area: "Area",
              pincode: "Pincode",
            }).map(([name, label]) => (
              <div className="col-md-6" key={name}>
                <label className="form-label">{label}</label>
                <input
                  name={name}
                  className="form-control"
                  value={form[name]}
                  onChange={(e) => change(name, e.target.value)}
                  required
                />
              </div>
            ))}
            <div className="col-12">
              <label className="form-label">Address type</label>
              <select
                name="type"
                className="form-select"
                value={form.type}
                onChange={(e) => change("type", e.target.value)}
              >
                <option>Home</option>
                <option>Work</option>
                <option>Other</option>
              </select>
            </div>
          </div>
          {error && <div className="alert alert-danger mt-3">{error}</div>}
          <PrimaryButton type="submit" className="mt-4" disabled={saving}>
            {saving ? "Saving…" : editing ? "Update address" : "Save address"}
          </PrimaryButton>
        </form>
      )}
    </div>
  );
}
